import { isoAttr } from "../../lib/datetime.ts";
import { useId, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { DenseTable, StatusBadge, type Column } from "../../components/index.ts";
import {
  failureDrillTo,
  failureLevel,
  failureStateLabel,
  failureStateTitle,
  formatCount,
  formatTime,
  type ProcessingFailure,
  type SystemHealth,
} from "./vocab.ts";

/**
 * Processing failures — the SEPARATE region (task 11.15; design 5.3 / 6.1, FR-14.6 / FR-14.7, 12.2
 * "per-source/instance/sequence processing failures with retry or dead-letter state", 12.3 / 12.4).
 *
 * This is the keystone of the screen and the reason it exists. The design is emphatic that a
 * batch&rsquo;s `202` DURABLE QUEUE ACCEPTANCE is INDEPENDENT of its later PROCESSING OUTCOME —
 * "the status row is written separately from the ingest 202; no processing outcome ever changes the
 * 202". So this region is physically and semantically DISTINCT from the queue-acceptance region: it
 * is NOT part of the acceptance tiles, it shares NO combined success/fail number with them, and it
 * is keyed on the batch IDENTITY (`source_id`, `instance_id`, `batch_seq`) rather than aggregated.
 *
 * Each row is one `ProcessingFailure` from `SystemHealth.processing_failures` (the SEPARATE list the
 * contract returns), showing:
 * - the batch identity: source, instance, batch sequence;
 * - the lifecycle STATE (icon+text) — a batch IN RETRY is clearly distinguished from a TERMINAL
 *   DEAD-LETTER by text, not colour alone (design 12.4): retry is a warn with the next-retry time,
 *   dead-letter is an error and is explicitly terminal;
 * - the ATTEMPT count;
 * - the NEXT-RETRY time (or "n/a" for a terminal / not-yet-scheduled batch);
 * - the failure reason the server recorded;
 * - the last-updated time.
 *
 * A row drills to the batch&rsquo;s source + instance in the Events view (design 12.3 "a processing
 * failure → /events?source=…&instance=…"). The region also carries the source / instance FILTER the
 * contract accepts on the list, and a cursor-paging control for `next_cursor`.
 */

export interface ProcessingFailuresProps {
  readonly page: SystemHealth["processing_failures"];
  /** The source filter currently applied (empty = all). */
  readonly sourceFilter: string;
  /** The instance filter currently applied (empty = all). */
  readonly instanceFilter: string;
  /** Apply the source / instance filter (empty string clears). */
  readonly onFilter: (filter: { sourceId: string; instanceId: string }) => void;
  /** Load the next page using `next_cursor`, if present. */
  readonly onNextPage: () => void;
  /** True when a cursor page beyond the first is being shown (so a "reset" is offered). */
  readonly isPaged: boolean;
  /** Reset to the first page / clear the cursor. */
  readonly onResetPage: () => void;
}

export function ProcessingFailures(props: ProcessingFailuresProps): ReactNode {
  const navigate = useNavigate();
  const { page } = props;

  // Worst first: terminal dead-letter, then retry, then in-flight; within a group, most attempts and
  // most recent update first. This surfaces the batches that gave up at the top.
  const rows = [...page.items].sort((a, b) => {
    const r = failureRank(a) - failureRank(b);
    if (r !== 0) return r;
    if (b.attempts !== a.attempts) return b.attempts - a.attempts;
    return b.updated_at - a.updated_at;
  });

  const columns: readonly Column<ProcessingFailure>[] = [
    {
      key: "batch",
      header: "Batch (source · instance · seq)",
      isRowHeader: true,
      cell: (f) => (
        <button
          type="button"
          className="sys-link sys-batch"
          onClick={() => navigate(failureDrillTo(f))}
          title={`See events for source ${f.source_id}, instance ${f.instance_id}`}
        >
          <code>{f.source_id}</code>
          <span className="sys-batch__sep"> · </span>
          <code>{f.instance_id}</code>
          <span className="sys-batch__sep"> · #</span>
          <code>{f.batch_seq.toLocaleString()}</code>
        </button>
      ),
    },
    {
      key: "state",
      header: "State",
      cell: (f) => (
        <StatusBadge level={failureLevel(f)} label={failureStateLabel(f)} size="sm" title={failureStateTitle(f)} />
      ),
    },
    {
      key: "attempts",
      header: "Attempts",
      align: "right",
      measure: "exact",
      cell: (f) => <span className="sys-num" data-emphasis={f.attempts > 1 ? "true" : undefined}>{formatCount(f.attempts)}</span>,
    },
    {
      key: "next_retry",
      header: "Next retry",
      align: "right",
      cell: (f) =>
        f.dead_lettered || f.state === "dead" ? (
          <span className="sys-muted" title="Terminal: no further attempts.">
            terminal
          </span>
        ) : f.next_retry_at === null ? (
          <span className="sys-muted">n/a</span>
        ) : (
          <time dateTime={isoAttr(f.next_retry_at)} title={isoAttr(f.next_retry_at)}>
            {formatTime(f.next_retry_at)}
          </time>
        ),
    },
    {
      key: "reason",
      header: "Reason",
      cell: (f) => (f.reason === null || f.reason.length === 0 ? <span className="sys-muted">n/a</span> : <span className="sys-reason">{f.reason}</span>),
    },
    {
      key: "updated",
      header: "Updated",
      align: "right",
      cell: (f) => (
        <time dateTime={isoAttr(f.updated_at)} title={isoAttr(f.updated_at)}>
          {formatTime(f.updated_at)}
        </time>
      ),
    },
  ];

  return (
    <section className="sys-region" id="sys-processing-failures" aria-label="Processing failures">
      <h2 className="sys-region__title">Processing failures</h2>
      <p className="sys-region__note sys-faint">
        Per-batch processing state, kept separate from queue acceptance: a batch was accepted with a
        durable 202 regardless of whether it later processed. Each batch is identified by source,
        instance and sequence, with its attempt count, next-retry time and terminal dead-letter
        state. A batch in retry will be attempted again; a dead-lettered batch will not.
      </p>

      <FilterBar
        sourceFilter={props.sourceFilter}
        instanceFilter={props.instanceFilter}
        onFilter={props.onFilter}
      />

      <DenseTable
        caption="One row per batch with a processing failure, identified by source, instance and sequence, with attempt, retry and terminal dead-letter state. Separate from the queue-acceptance view above."
        columns={columns}
        rows={rows}
        rowKey={(f) => `${f.source_id}:${f.instance_id}:${f.batch_seq}`}
        stickyHeader
        emptyMessage={
          props.sourceFilter.length > 0 || props.instanceFilter.length > 0
            ? "No processing failures for this source/instance in this window. Clear the filter to see all batches."
            : "No batches have failed processing in this window. Accepted batches are moving through the queue normally."
        }
      />

      <div className="sys-pager" role="group" aria-label="Processing-failure paging">
        {props.isPaged && (
          <button type="button" className="sys-btn" onClick={props.onResetPage}>
            ← First page
          </button>
        )}
        {page.next_cursor !== null && (
          <button type="button" className="sys-btn" onClick={props.onNextPage}>
            More failures →
          </button>
        )}
      </div>
    </section>
  );
}

/** Source / instance filter — the two fields the contract accepts on the processing-failure list. */
function FilterBar(props: {
  sourceFilter: string;
  instanceFilter: string;
  onFilter: (filter: { sourceId: string; instanceId: string }) => void;
}): ReactNode {
  const sourceId = useId();
  const instanceId = useId();

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    props.onFilter({
      sourceId: String(data.get("source_id") ?? "").trim(),
      instanceId: String(data.get("instance_id") ?? "").trim(),
    });
  };

  const hasFilter = props.sourceFilter.length > 0 || props.instanceFilter.length > 0;

  return (
    <form className="sys-filter" onSubmit={onSubmit} aria-label="Filter processing failures">
      <div className="sys-filter__field">
        <label htmlFor={sourceId}>Source</label>
        <input
          id={sourceId}
          name="source_id"
          type="text"
          defaultValue={props.sourceFilter}
          placeholder="any source"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <div className="sys-filter__field">
        <label htmlFor={instanceId}>Instance</label>
        <input
          id={instanceId}
          name="instance_id"
          type="text"
          defaultValue={props.instanceFilter}
          placeholder="any instance"
          autoComplete="off"
          spellCheck={false}
        />
      </div>
      <button type="submit" className="sys-btn">
        Apply
      </button>
      {hasFilter && (
        <button
          type="button"
          className="sys-btn sys-btn--ghost"
          onClick={() => props.onFilter({ sourceId: "", instanceId: "" })}
        >
          Clear
        </button>
      )}
    </form>
  );
}

/** Worst-first ordering: dead-letter (0) < retry (1) < in-flight (2) < succeeded (3). */
function failureRank(f: ProcessingFailure): number {
  if (f.dead_lettered || f.state === "dead") return 0;
  if (f.state === "retry") return 1;
  if (f.state === "queued" || f.state === "processing") return 2;
  return 3;
}
